// CRM 2.0 · M12-F4 · one agreement (DC-222): the scanner while the copy is with me (camera on at once, a sheet per
// QR page, Looks good / Not matching, "Agreement checked" after the last page; the page list says which pages are
// left), or View of a signed copy (the values I checked on each page, as they were then).
// Not matching: after Save, send back to the Document Desk now or check the other pages; while scanning a bar says
// "N pages not matching · Send back now"; every page answered with any Not matching: the end screen sends it back.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./Crm2Verify.module.css";
import QrCamera from "./QrCamera";
import SheetGroups, { groupsOfShown } from "./SheetGroups";
import { Progress } from "./VerifyAgreements";
import { getMyAgreement, scanForAgreement, checkAgreementPage, sendBackAgreement } from "../../Services/Crm2Verify";
import { errorTost } from "../../Services/Toast";

const STATE = { CHECKED: ["Checked", cls.stOk], NOT_MATCHING: ["Not matching", cls.stBad], PENDING: ["To scan", cls.stTodo] };
const pageName = (p) => (p.pageCode === "TRACKING" ? (p.topSheet ? "Top sheet" : "Page 1 · Copy QR") : `Page ${p.pageNo}`);
const pagesText = (n) => `${n} ${n === 1 ? "page" : "pages"} not matching`;

// the Not matching pages so far, each with the note
function NotMatchingList({ pages }) {
  return (
    <ul className={cls.nmList}>
      {(pages || []).map((p) => (
        <li key={p.pageCode}>
          <b>{pageName(p)}</b>
          <span>{p.note || "—"}</span>
        </li>
      ))}
    </ul>
  );
}

function SignedView({ a, onBack }) {
  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <h1 className={cls.title}>{a.customer || "Agreement"}</h1>
        <p className={cls.who}>{a.unit} · Copy {a.copyNo}</p>
      </header>
      <div className={cls.stack}>
        <div className={cls.card}>
          <p className={cls.meta}>Signed by me · {a.signedOn || "—"}</p>
          <Progress checked={a.checked} total={a.total} />
        </div>
        {a.pages.map((p) => (
          <div key={`${p.pageNo}-${p.tracking ? "t" : "p"}`} className={cls.stack}>
            <div className={cls.pageHead}>
              <span>{p.topSheet ? "Top sheet · Copy QR" : `Page ${p.pageNo}${p.tracking ? " · Copy QR" : ""}`}</span>
              <span className={`${cls.st} ${p.result === "NOT_MATCHING" ? cls.stBad : cls.stOk}`}>{p.result === "NOT_MATCHING" ? "Not matching" : `Checked ${p.checkedOn}`}</span>
            </div>
            {p.note && <p className={cls.reason}>{p.note}</p>}
            <SheetGroups groups={groupsOfShown(p.values)} />
          </div>
        ))}
      </div>
      <div className={cls.dock}>
        <button className={`${cls.btn} ${cls.wide}`} onClick={onBack}>Back to the list</button>
      </div>
    </div>
  );
}

function AgreementScanner() {
  const { copyId } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [sheet, setSheet] = useState(null); // the page shown after a scan
  const [toast, setToast] = useState(null); // { text, old }
  const [asking, setAsking] = useState(false); // Not matching: the reason box
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [showPages, setShowPages] = useState(false); // the list of QR pages: which ones are still to scan
  const [marked, setMarked] = useState(null); // the page just saved as Not matching: send back now, or check others
  const [sentBack, setSentBack] = useState(null); // the copy went back to the Document Desk: the message
  const reading = useRef(false);
  const toastTimer = useRef(null);
  const back = () => navigate("/crm2/verify-agreements");

  useEffect(() => {
    let live = true;
    getMyAgreement(copyId).then((d) => live && setA(d)).catch((e) => { errorTost(e.message); back(); });
    return () => { live = false; clearTimeout(toastTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyId]);

  const say = (text, old = false) => {
    setToast({ text, old });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), old ? 4000 : 2500);
  };

  const onRead = useCallback(async (text) => {
    if (reading.current) return;
    reading.current = true;
    try {
      const r = await scanForAgreement(copyId, text);
      if (r.ok) setSheet(r);
      else say(r.message, !!r.old);
    } catch (e) {
      say(e.message);
    } finally {
      reading.current = false;
    }
  }, [copyId]);

  async function check(result) {
    setBusy(true);
    try {
      const r = await checkAgreementPage(copyId, { pageCode: sheet.pageCode, result, note: result === "NOT_MATCHING" ? reason.trim() : null });
      const pages = r.notMatchingPages || [];
      setA((x) => ({ ...x, checked: r.checked, total: r.total, notMatching: pages.length, notMatchingPages: pages, allScanned: r.allScanned }));
      getMyAgreement(copyId).then((d) => d && setA(d)).catch(() => {});
      const page = { pageNo: sheet.pageNo, pageCode: sheet.pageCode, topSheet: sheet.pageCode === "TRACKING" && (a.pageList || []).some((p) => p.tracking && p.topSheet) };
      setSheet(null);
      setAsking(false);
      setReason("");
      if (r.done) setDone(r.message);
      // every page answered and some do not match: the end screen (below) takes over
      else if (r.allScanned && pages.length) setMarked(null);
      else if (result === "NOT_MATCHING") setMarked(page);
      else say(`${pageName(page)} checked · ${r.checked} of ${r.total}. Scan the next QR.`);
    } catch (e) {
      say(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Send back to the Document Desk: the pages marked Not matching so far; pages not scanned stay unchecked
  async function sendBack() {
    setBusy(true);
    try {
      const r = await sendBackAgreement(copyId);
      setMarked(null);
      setShowPages(false);
      setSentBack(r);
    } catch (e) {
      say(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!a) return <div className={cls.page}><div className={cls.empty}>Loading…</div></div>;
  if (!a.canScan && !done && !sentBack) return <SignedView a={a} onBack={back} />;
  const pct = a.total ? Math.round((a.checked / a.total) * 100) : 0;
  const left = (a.pageList || []).filter((p) => p.state !== "CHECKED");
  const nm = a.notMatchingPages || [];
  // every QR page answered and any Not matching: never "Agreement checked", only send back
  const endScreen = !done && !sentBack && a.allScanned && nm.length > 0;
  const finished = !!(done || sentBack || endScreen);

  return (
    <div className={cls.scanner}>
      <div className={cls.topBar}>
        <div className={cls.topInfo}>
          <p className={cls.topUnit}>{a.customer || a.unit}</p>
          <p className={cls.topSub}>{a.unit} · Copy {a.copyNo}{a.resign ? " · Re-sign" : ""}</p>
        </div>
        {!finished && (
          <button className={cls.chipBtn} onClick={() => setShowPages((x) => !x)} aria-expanded={showPages} aria-label="QR pages">
            {a.checked}/{a.total} ▾
          </button>
        )}
        <button className={cls.chipBtn} onClick={back}>Close</button>
      </div>
      <div className={cls.topProgress} aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>

      {/* everything below the top bar: the pop-ups open here, so they never cover the agreement's name and copy */}
      <div className={cls.stage}>
      {showPages && !finished && <div className={`${cls.backdrop} ${cls.backdropPanel}`} onClick={() => setShowPages(false)} aria-hidden="true" />}
      {showPages && !finished && (
        <div className={cls.panel} role="dialog" aria-label="QR pages">
          <div className={cls.panelHead}>
            <b>QR pages</b>
            <span className={cls.meta}>
              {left.length ? `${left.length} left to scan${left.some((p) => p.topSheet) ? " · Scan the top sheet too" : ""}` : "All checked"}
            </span>
          </div>
          <ul className={cls.pages}>
            {(a.pageList || []).map((p, i) => (
              <li key={`${p.pageNo}-${p.tracking ? "t" : "p"}-${i}`}>
                <span className={cls.pageName}>
                  <span>{p.topSheet ? "Top sheet" : `Page ${p.pageNo}`}</span>
                  {p.title && <small>{p.title}</small>}
                </span>
                <span className={`${cls.st} ${STATE[p.state][1]}`}>{STATE[p.state][0]}</span>
              </li>
            ))}
          </ul>
          <button className={`${cls.btn} ${cls.wide}`} onClick={() => setShowPages(false)}>Back to scanning</button>
        </div>
      )}

      {done ? (
        <div className={cls.done}>
          <div className={cls.tick} aria-hidden="true">✓</div>
          <p className={cls.doneTitle}>Agreement checked</p>
          <p className={cls.doneText}>{done.replace(/^Agreement checked\. /, "")}</p>
          <button className={`${cls.btn} ${cls.primary} ${cls.wide}`} onClick={back}>Back to the list</button>
        </div>
      ) : sentBack ? (
        <div className={cls.done}>
          <p className={cls.doneTitle}>Sent back to the Document Desk</p>
          <p className={cls.doneText}>{sentBack.message} The Desk prints a new copy and hands it to you; you check it from the start.</p>
          <NotMatchingList pages={sentBack.pages} />
          <button className={`${cls.btn} ${cls.primary} ${cls.wide}`} onClick={back}>Back to the list</button>
        </div>
      ) : endScreen ? (
        <div className={cls.done}>
          <p className={cls.doneTitle}>{pagesText(nm.length)}</p>
          <p className={cls.doneText}>Every QR page is scanned. Do not sign this copy: send it back, and the Desk prints a new copy.</p>
          <NotMatchingList pages={nm} />
          <button className={`${cls.btn} ${cls.primary} ${cls.wide}`} disabled={busy} onClick={sendBack}>Send back to Document Desk</button>
          {toast && <p className={cls.reason} role="status">{toast.text}</p>}
        </div>
      ) : (
        <>
          <QrCamera paused={!!sheet || showPages || !!marked} onRead={onRead} hint="Point the camera at a QR on the agreement" />
          {/* while scanning after a Not matching page: stop at any time */}
          {nm.length > 0 && !sheet && !marked && (
            <div className={cls.nmBar} role="status">
              <span>{pagesText(nm.length)}</span>
              <button className={cls.nmBarBtn} disabled={busy} onClick={sendBack}>Send back now</button>
            </div>
          )}
          {toast && <div className={`${cls.toast} ${toast.old ? cls.toastOld : ""}`} role="status">{toast.text}</div>}
          {marked && <div className={cls.backdrop} aria-hidden="true" />}
          {marked && (
            <div className={cls.sheet} role="dialog" aria-label="Not matching saved">
              <div className={cls.handle} aria-hidden="true" />
              <div className={cls.sheetTop}>
                <p className={cls.sheetStep}>Not matching saved</p>
                <h4 className={cls.sheetTitle}>{pageName(marked)} does not match</h4>
                <p className={cls.sheetSub}>{pagesText(nm.length)} so far · do not sign this copy</p>
              </div>
              <div className={cls.sheetBody}>
                <NotMatchingList pages={nm} />
              </div>
              <div className={cls.sheetFoot}>
                <button className={`${cls.btn} ${cls.primary} ${cls.wide}`} disabled={busy} onClick={sendBack}>Send back to Document Desk</button>
                <button className={cls.linkBtn} disabled={busy} onClick={() => setMarked(null)}>Check other pages</button>
              </div>
            </div>
          )}
          {sheet && <div className={cls.backdrop} aria-hidden="true" />}
          {sheet && (
            <div className={cls.sheet} role="dialog" aria-label={sheet.title}>
              <div className={cls.handle} aria-hidden="true" />
              {/* the page title stays at the top while the values scroll */}
              <div className={cls.sheetTop}>
                <p className={cls.sheetStep}>{sheet.pageCode === "TRACKING" ? "Compare with the printed top sheet" : "Compare with the printed page"}</p>
                <h4 className={cls.sheetTitle}>{sheet.title}</h4>
                {sheet.summary && <p className={cls.sheetSub}>{sheet.summary}</p>}
                {sheet.changed && <p className={cls.sheetChanged}>{sheet.changed}</p>}
              </div>
              <div className={cls.sheetBody}>
                {/* an earlier QR skipped: a reminder only, this page can still be checked */}
                {sheet.missed && <p className={cls.missed} role="status">{sheet.missed}</p>}
                {sheet.alreadyChecked && <p className={cls.notice}>{sheet.alreadyChecked}</p>}
                <SheetGroups groups={sheet.groups} />
                {sheet.masked && <p className={cls.masked}>PAN, Aadhaar, mobile and email are hidden for your role.</p>}
              </div>
              {/* the answers stay at the bottom of the sheet, always in view */}
              <div className={cls.sheetFoot}>
                {sheet.alreadyChecked ? (
                  // checked before: the values are shown to cross-check again; nothing to answer
                  <button className={`${cls.btn} ${cls.primary} ${cls.wide}`} onClick={() => setSheet(null)}>Close · next QR</button>
                ) : asking ? (
                  <>
                    <input className={cls.input} value={reason} autoFocus onChange={(e) => setReason(e.target.value)} placeholder="What does not match?" aria-label="What does not match" />
                    <div className={cls.footRow}>
                      <button className={cls.btn} onClick={() => { setAsking(false); setReason(""); }}>Cancel</button>
                      <button className={`${cls.btn} ${cls.primary}`} disabled={busy || !reason.trim()} onClick={() => check("NOT_MATCHING")}>Save</button>
                    </div>
                  </>
                ) : (
                  <div className={cls.footRow}>
                    <button className={cls.btn} disabled={busy} onClick={() => setAsking(true)}>Not matching</button>
                    <button className={`${cls.btn} ${cls.primary}`} disabled={busy} onClick={() => check("OK")}>Looks good · next QR</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

export default AgreementScanner;
